import { Loader } from '@beholos/holoscommon';
import { useState, useEffect, useRef } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { submitComplaint } from '../../services/complaints.service';

interface PlaceData {
  state: string;
  region?: string;
  county?: string;
  ubigeo: string;
}

interface SelectOption {
  value: string;
  children: string;
  disabled?: boolean;
  key: number | string;
}

interface FormData {
  customer: Record<string, string>;
  asset: Record<string, string>;
  reason: Record<string, string>;
}

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const inputErrorClass = "w-full px-3 py-2 border border-red-500 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent";
const selectClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white";
const selectErrorClass = "w-full px-3 py-2 border border-red-500 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white";
const labelClass = "block font-bold uppercase text-left mb-2 text-sm";
const textareaClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none";
const textareaErrorClass = "w-full px-3 py-2 border border-red-500 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none";

export const Complaints = () => {
  const placesRef = useRef<PlaceData[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    customer: {},
    asset: {},
    reason: {},
  });

  const [documents] = useState<SelectOption[]>([
    { value: '', children: 'Seleccionar', disabled: true, key: 0 },
    { value: 'DNI', children: 'DNI', key: 1 },
    { value: 'CE', children: 'Carné de extranjería', key: 2 },
  ]);

  const [states, setStates] = useState<SelectOption[]>([
    { value: '', children: 'Seleccionar', disabled: true, key: 0 }
  ]);
  const [regions, setRegions] = useState<SelectOption[]>([
    { value: '', children: 'Seleccionar', disabled: true, key: 0 }
  ]);
  const [counties, setCounties] = useState<SelectOption[]>([
    { value: '', children: 'Seleccionar', disabled: true, key: 0 }
  ]);

  const [submitted, setSubmitted] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  useEffect(() => {
    const loadPlaces = async () => {
      try {
        // Dynamic import for peru locations data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const module = await import('./peru' as any);
        const places = module.default as PlaceData[];
        placesRef.current = places;

        const stateMap = places.reduce<Record<string, SelectOption>>((acc, { state, ubigeo }) => {
          if (!acc[state]) {
            acc[state] = { value: state, children: state, key: ubigeo };
          }
          return acc;
        }, {});

        const stateOptions = Object.values(stateMap);
        stateOptions.unshift({ value: '', children: 'Seleccionar', disabled: true, key: 0 });
        setStates(stateOptions);
      } catch (error) {
        console.error('Error loading places:', error);
      }
    };
    loadPlaces();
  }, []);

  const getNextPlaces = (matchField: string, nameField: string, parentValue: string) => {
    const collection: Record<string, SelectOption> = {};
    placesRef.current.forEach((place) => {
      const placeRecord = place as unknown as Record<string, string>;
      if (placeRecord[matchField] === parentValue && placeRecord[nameField]) {
        const value = placeRecord[nameField];
        collection[value] = { value, key: place.ubigeo, children: value };
      }
    });
    const options = Object.values(collection);
    options.unshift({ value: '', children: 'Seleccionar', disabled: true, key: 0 });
    return options;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const field = e.currentTarget;
    const { id, name } = field;
    const isRadio = field.type === 'radio';
    const value = isRadio ? (field as HTMLInputElement).checked ? field.value : '' : field.value;
    const own = field.dataset.own || '';
    const next = field.dataset.next;
    const nextUid = field.dataset.nextUid;

    setFormData(prev => {
      const newData = {
        ...prev,
        [own]: {
          ...prev[own as keyof FormData],
          [name || id]: value,
        },
      };

      if (next && nextUid) {
        const newOptions = getNextPlaces(name || id, nextUid, value);
        if (next === 'regions') {
          setRegions(newOptions);
          setCounties([{ value: '', children: 'Seleccionar', disabled: true, key: 0 }]);
          newData.customer.region = '';
          newData.customer.county = '';
        } else if (next === 'counties') {
          setCounties(newOptions);
          newData.customer.county = '';
        }
      }

      return newData;
    });

    // Update list of invalid fields for display only if validation is already shown
    if (showValidation) {
      const form = e.target.form;
      if (form) {
        const invalids: string[] = [];
        const elements = form.elements;
        for (let i = 0; i < elements.length; i++) {
          const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
          if (el.name || el.id) {
            if (!el.validity.valid) {
              invalids.push(el.id || el.name);
            }
          }
        }
        setInvalidFields(invalids);
      }
    }
  };

  const getInputClass = (fieldId: string) => {
    return showValidation && invalidFields.includes(fieldId) ? inputErrorClass : inputClass;
  };

  const getSelectClass = (fieldId: string) => {
    return showValidation && invalidFields.includes(fieldId) ? selectErrorClass : selectClass;
  };

  const getTextareaClass = (fieldId: string) => {
    return showValidation && invalidFields.includes(fieldId) ? textareaErrorClass : textareaClass;
  };

  const getFieldLabel = (fieldId: string): string => {
    const labels: Record<string, string> = {
      name: 'Nombres',
      surname: 'Apellidos',
      cellphone: 'Celular',
      phone: 'Teléfono Fijo',
      identityType: 'Tipo de Documento',
      identityNumber: 'Nro. Documento',
      email: 'Email',
      state: 'Departamento',
      region: 'Provincia',
      county: 'Distrito',
      address: 'Dirección',
      type: 'Tipo de bien contratado',
      amount: 'Monto',
      description: 'Descripción',
      detail: 'Detalle',
      request: 'Pedido',
    };
    return labels[fieldId] || fieldId;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowValidation(true);

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      // Collect invalid fields
      const invalids: string[] = [];
      const elements = form.elements;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (el.name || el.id) {
          if (!el.validity.valid) {
            invalids.push(el.id || el.name);
          }
        }
      }
      setInvalidFields(invalids);
      return;
    }

    setSubmitted(true);
    setLoading(true);

    try {
      const data = await submitComplaint(formData);

      setCode(data.code);
    } catch (error) {
      console.error('Error submitting complaint:', error);
      setSubmitted(false);
      setLoading(false);
      setError('Se ha producido un error al enviar el reclamo. Por favor, inténtelo de nuevo más tarde o contacte con soporte.');
    }
  };

  if (submitted && code) {
    return (
      <div className="py-15 flex flex-col items-center justify-center px-4">
        <div className="text-2xl font-semibold mb-2 text-holos-dark">¡Hola {formData.customer.name}!</div>
        <h2 className="w-full text-center pb-5 text-md">
          <br />
          Gracias por llenar este formulario.
          <br />
          Tu código de reclamo es: <span className="font-bold">{code}</span>
          <br />
          Pronto nos pondremos en contacto contigo.
        </h2>
        <a
          href="/terminos"
          className="block text-white bg-holos-dark hover:bg-holos-dark-dark/70 uppercase my-4 mx-auto py-3 px-6 rounded-md text-center max-w-xs"
        >
          Ver términos y condiciones
        </a>
      </div>
    );
  }

  return (
    <form
      acceptCharset="UTF-8"
      autoComplete="off"
      className="max-w-3xl mx-auto px-4"
      method="post"
      onSubmit={handleSubmit}
    >
      <fieldset disabled={submitted}>
        <h4 className="py-4 text-3xl font-juana font-bold text-center mt-10 mb-3">Ingresa tus datos para poder ayudarte</h4>
        <p className="text-sm text-gray-500 mb-4"><span className="text-red-500">*</span> campo requerido</p>

        {/* Customer Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className={labelClass}>Nombres <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="name"
              data-own="customer"
              placeholder="Nombres"
              required
              maxLength={100}
              pattern="([A-z0-9À-ž\s]){2,100}"
              className={getInputClass('name')}
              value={formData.customer.name || ''}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="surname" className={labelClass}>Apellidos <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="surname"
              data-own="customer"
              placeholder="Apellidos"
              required
              maxLength={100}
              pattern="([A-z0-9À-ž\s]){2,100}"
              className={getInputClass('surname')}
              value={formData.customer.surname || ''}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="cellphone" className={labelClass}>Celular <span className="text-red-500">*</span></label>
            <input
              type="tel"
              id="cellphone"
              data-own="customer"
              placeholder="Celular"
              required
              maxLength={12}
              pattern="[+ 0-9]{7,12}"
              className={getInputClass('cellphone')}
              value={formData.customer.cellphone || ''}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="phone" className={labelClass}>Teléfono Fijo <span className="text-red-500">*</span></label>
            <input
              type="tel"
              id="phone"
              data-own="customer"
              placeholder="Teléfono fijo"
              required
              maxLength={12}
              pattern="[+ 0-9]{7,12}"
              className={getInputClass('phone')}
              value={formData.customer.phone || ''}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="identityType" className={labelClass}>Tipo de Documento <span className="text-red-500">*</span></label>
            <select
              id="identityType"
              data-own="customer"
              required
              className={getSelectClass('identityType')}
              value={formData.customer.identityType || ''}
              onChange={handleChange}
            >
              {documents.map((opt) => (
                <option key={opt.key} value={opt.value} disabled={opt.disabled}>
                  {opt.children}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="identityNumber" className={labelClass}>Nro. Documento <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="identityNumber"
              data-own="customer"
              placeholder="Nro. Documento"
              required
              maxLength={12}
              pattern="[+ 0-9]{8,12}"
              className={getInputClass('identityNumber')}
              value={formData.customer.identityNumber || ''}
              onChange={handleChange}
            />
          </div>
          <div className="sm:col-span-2 md:col-span-1">
            <label htmlFor="email" className={labelClass}>Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              id="email"
              data-own="customer"
              placeholder="ejemplo@domain.com"
              required
              maxLength={120}
              className={getInputClass('email')}
              value={formData.customer.email || ''}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="state" className={labelClass}>Departamento <span className="text-red-500">*</span></label>
            <select
              id="state"
              name="state"
              data-own="customer"
              data-next="regions"
              data-next-uid="region"
              required
              className={getSelectClass('state')}
              value={formData.customer.state || ''}
              onChange={handleChange}
            >
              {states.map((opt) => (
                <option key={opt.key} value={opt.value} disabled={opt.disabled}>
                  {opt.children}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="region" className={labelClass}>Provincia <span className="text-red-500">*</span></label>
            <select
              id="region"
              name="region"
              data-own="customer"
              data-next="counties"
              data-next-uid="county"
              required
              className={getSelectClass('region')}
              value={formData.customer.region || ''}
              onChange={handleChange}
            >
              {regions.map((opt) => (
                <option key={opt.key} value={opt.value} disabled={opt.disabled}>
                  {opt.children}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="county" className={labelClass}>Distrito <span className="text-red-500">*</span></label>
            <select
              id="county"
              data-own="customer"
              required
              className={getSelectClass('county')}
              value={formData.customer.county || ''}
              onChange={handleChange}
            >
              {counties.map((opt) => (
                <option key={opt.key} value={opt.value} disabled={opt.disabled}>
                  {opt.children}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="address" className={labelClass}>Dirección <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="address"
              data-own="customer"
              placeholder="Ingrese su dirección actual"
              required
              maxLength={120}
              pattern="([A-z0-9À-ž\s]){2,120}"
              className={getInputClass('address')}
              value={formData.customer.address || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Parent Info */}
        <h6 className="py-4 text-base">En caso sea menor de edad</h6>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="parent_name" className={labelClass}>Nombre del Padre o Madre</label>
            <input
              type="text"
              id="parent_name"
              data-own="customer"
              placeholder="Nombre del padre o madre"
              maxLength={200}
              pattern="([A-z0-9À-ž\s]){2,200}"
              className={getInputClass('parent_name')}
              value={formData.customer.parent_name || ''}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="parent_phone" className={labelClass}>Teléfono del Padre o Madre</label>
            <input
              type="tel"
              id="parent_phone"
              data-own="customer"
              placeholder="Teléfono del padre o madre"
              maxLength={12}
              pattern="[+ 0-9]{7,12}"
              className={getInputClass('parent_phone')}
              value={formData.customer.parent_phone || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Asset Info */}
        <div className="py-4">
          <div className="mb-4">
            <label className={labelClass}>Tipo de bien contratado <span className="text-red-500">*</span></label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  id="product"
                  name="type"
                  value="Producto"
                  data-own="asset"
                  required
                  className="w-4 h-4"
                  checked={formData.asset.type === 'Producto'}
                  onChange={handleChange}
                />
                <span className="font-medium uppercase">Producto</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  id="service"
                  name="type"
                  value="Servicio"
                  data-own="asset"
                  required
                  className="w-4 h-4"
                  checked={formData.asset.type === 'Servicio'}
                  onChange={handleChange}
                />
                <span className="font-medium uppercase">Servicio</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="amount" className={labelClass}>Monto (S/.) <span className="text-red-500">*</span></label>
              <input
                type="text"
                id="amount"
                data-own="asset"
                placeholder="Ingresar monto (S/)"
                required
                maxLength={10}
                pattern="^\$?(([1-9](\d*|\d{0,2}(,\d{3})*))|0)(\.\d{1,2})?$"
                className={getInputClass('amount')}
                value={formData.asset.amount || ''}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="description" className={labelClass}>Descripción <span className="text-red-500">*</span></label>
            <textarea
              id="description"
              data-own="asset"
              placeholder="Descripción del tipo de bien contratado"
              required
              maxLength={200}
              rows={3}
              className={getTextareaClass('description')}
              value={formData.asset.description || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Reason Info */}
        <h3 className="pt-4 pb-2 text-lg font-bold">¿Qué fue lo que sucedió?</h3>
        <p className="m-0 text-sm">
          <span className="font-bold">Reclamo:&nbsp;</span>
          Disconformidad relacionada a los productos o servicios.
        </p>
        <p className="m-0 text-sm">
          <span className="font-bold">Queja:&nbsp;</span>
          Disconformidad no relacionada a los productos o servicios; o, malestar o descontento respecto a la atención al público.
        </p>

        <div className="py-4">
          <div className="mb-4">
            <label className={labelClass}>Tipo de reclamo <span className="text-red-500">*</span></label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  id="claim"
                  name="name"
                  value="Reclamo"
                  data-own="reason"
                  required
                  className="w-4 h-4"
                  checked={formData.reason.name === 'Reclamo'}
                  onChange={handleChange}
                />
                <span className="font-medium uppercase">Reclamo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  id="grievance"
                  name="name"
                  value="Queja"
                  data-own="reason"
                  required
                  className="w-4 h-4"
                  checked={formData.reason.name === 'Queja'}
                  onChange={handleChange}
                />
                <span className="font-medium uppercase">Queja</span>
              </label>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="detail" className={labelClass}>Detalle <span className="text-red-500">*</span></label>
            <textarea
              id="detail"
              data-own="reason"
              placeholder="Detalle de la reclamación"
              required
              maxLength={200}
              rows={3}
              className={getTextareaClass('detail')}
              value={formData.reason.detail || ''}
              onChange={handleChange}
            />
            <span className="text-xs text-gray-500">Máx: 200 caracteres</span>
          </div>
          <div className="mb-4">
            <label htmlFor="request" className={labelClass}>Pedido <span className="text-red-500">*</span></label>
            <textarea
              id="request"
              data-own="reason"
              placeholder="Pedido del consumidor"
              required
              maxLength={200}
              rows={3}
              className={getTextareaClass('request')}
              value={formData.reason.request || ''}
              onChange={handleChange}
            />
            <span className="text-xs text-gray-500">Máx: 200 caracteres</span>
          </div>
        </div>


        <div className="py-4">
          <p className="text-center text-sm text-gray-600">
            De no ingresar la información solicitada no será posible atender su reclamo, de conformidad con el
            reglamento del Libro de Reclamaciones, aprobado mediante Decreto Supremo N° 011-2011-PCM.
          </p>

          {error && (
            <div className="my-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {showValidation && invalidFields.length > 0 && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-400 text-yellow-800 rounded-md">
              <p className="font-bold mb-2">Por favor complete los siguientes campos:</p>
              <ul className="list-disc list-inside">
                {invalidFields.map((field) => (
                  <li key={field}>{getFieldLabel(field)}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-center">
            <button
              type="submit"
              disabled={loading}
              className="text-white uppercase my-4 py-3 px-8 rounded-md max-w-[200px] bg-holos-dark hover:bg-holos-dark/70 cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              ENVIAR
            </button>
          </div>

          {loading && <div className="py-3 flex justify-center">
            <Loader />
          </div>}
        </div>
      </fieldset>
    </form>
  );
}